import {
    createColumnHelper,
} from "@tanstack/react-table";

import {
    FiSend,
} from "react-icons/fi";

import {
    FaRupeeSign,
} from "react-icons/fa";

import CrudActions from "components/crud/CrudActions";

const columnHelper =
    createColumnHelper();


/*
|--------------------------------------------------------------------------
| DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


/*
|--------------------------------------------------------------------------
| CURRENCY
|--------------------------------------------------------------------------
*/

const formatCurrency = (
    value
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    const amount =
        Number(value);

    if (
        Number.isNaN(amount)
    ) {
        return `₹${value}`;
    }

    return `₹${amount.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

const getStatusClass = (
    status
) => {

    switch (status) {

        case "draft":
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;

        case "submitted":
            return `
                border-blue-200
                bg-blue-50
                text-blue-700
            `;

        case "approved":
            return `
                border-green-200
                bg-green-50
                text-green-700
            `;

        case "rejected":
            return `
                border-red-200
                bg-red-50
                text-red-700
            `;

        case "paid":
            return `
                border-emerald-200
                bg-emerald-50
                text-emerald-700
            `;

        case "cancelled":
            return `
                border-orange-200
                bg-orange-50
                text-orange-700
            `;

        default:
            return `
                border-gray-200
                bg-gray-50
                text-gray-700
            `;
    }
};


export const taDaColumns = ({
    onEdit,
    onCancel,
    onSubmit,
    onApprove,
    onReject,
    onMarkPaid,
    onView,
} = {}) => {

    let user = {};

    try {

        user =
            JSON.parse(
                localStorage.getItem(
                    "user"
                ) || "{}"
            );

    } catch (error) {

        console.error(
            "Unable to parse user",
            error
        );

    }


    const permissions =
        Array.isArray(
            user?.permissions
        )
            ? user.permissions
            : [];


    const roles =
        Array.isArray(
            user?.roles
        )
            ? user.roles
            : [];


    const hasPermission = (
        permission
    ) =>
        permissions.includes(
            permission
        );


    const isAdmin =
        roles.includes(
            "admin"
        ) ||
        roles.includes(
            "super_admin"
        );


    const isManager =
        roles.includes(
            "manager"
        ) ||
        roles.includes(
            "team_leader"
        ) ||
        roles.includes(
            "regional_manager"
        );


    const isSalesPerson =
        roles.includes(
            "sales_person"
        );


    const canSalesActions =
        isSalesPerson &&
        (
            hasPermission(
                "ta_da.update"
            ) ||
            hasPermission(
                "ta_da.manage"
            )
        );


    const canCancel =
        isSalesPerson &&
        (
            hasPermission(
                "ta_da.delete"
            ) ||
            hasPermission(
                "ta_da.manage"
            )
        );


    const canReview =
        (
            isAdmin ||
            isManager
        ) &&
        (
            hasPermission(
                "ta_da.approve"
            ) ||
            hasPermission(
                "ta_da.manage"
            )
        );


    const canMarkPaid =
        isAdmin &&
        (
            hasPermission(
                "ta_da.approve"
            ) ||
            hasPermission(
                "ta_da.manage"
            )
        );


    return [

        // =========================================================
        // CLAIM DATE
        // =========================================================

        columnHelper.accessor(
            "claimDate",
            {
                id: "claimDate",

                header: "Claim Date",

                cell: (info) => (
                    formatDate(
                        info.getValue()
                    )
                ),
            }
        ),


        // =========================================================
        // EMPLOYEE
        // =========================================================

        columnHelper.accessor(
            "employee.name",
            {
                id: "employee",

                header: "Employee",

                cell: (info) => (
                    <span className="font-semibold text-white-700">
                        {info.getValue() || "-"}
                    </span>
                ),
            }
        ),


        // =========================================================
        // TITLE
        // =========================================================

        columnHelper.accessor(
            "title",
            {
                id: "title",

                header: "Title",

                cell: (info) => (
                    <span>
                        {info.getValue() || "-"}
                    </span>
                ),
            }
        ),


        // =========================================================
        // CLAIMED
        // =========================================================

        columnHelper.accessor(
            "claimedAmount",
            {
                id: "claimedAmount",

                header: "Claimed",

                cell: (info) => (
                    <span className="font-semibold">
                        {formatCurrency(
                            info.getValue()
                        )}
                    </span>
                ),
            }
        ),


        // =========================================================
        // APPROVED
        // =========================================================

        columnHelper.accessor(
            "approvedAmount",
            {
                id: "approvedAmount",

                header: "Approved",

                cell: (info) => (
                    <span className="font-semibold">
                        {formatCurrency(
                            info.getValue()
                        )}
                    </span>
                ),
            }
        ),


        // =========================================================
        // STATUS
        // =========================================================

        columnHelper.accessor(
            "status",
            {
                id: "status",

                header: "Status",

                cell: (info) => {

                    const status =
                        info.getValue();

                    return (
                        <span
                            className={`
                                inline-flex
                                items-center
                                justify-center
                                whitespace-nowrap
                                rounded-full
                                border
                                px-3
                                py-1
                                text-xs
                                font-semibold
                                capitalize
                                ${getStatusClass(
                                    status
                                )}
                            `}
                        >
                            {
                                status
                                    ? String(
                                        status
                                    ).replace(
                                        /_/g,
                                        " "
                                    )
                                    : "-"
                            }
                        </span>
                    );
                },
            }
        ),


        // =========================================================
        // ACTIONS
        // =========================================================

        columnHelper.display({

            id: "actions",

            header: "Actions",

            cell: ({ row }) => {

                const claim =
                    row.original;

                const canEdit =
                    canSalesActions &&
                    (
                        claim.status ===
                            "draft" ||
                        claim.status ===
                            "rejected"
                    );


                const canSubmit =
                    canSalesActions &&
                    (
                        claim.status ===
                            "draft" ||
                        claim.status ===
                            "rejected"
                    );


                const canCancelClaim =
                    canCancel &&
                    (
                        claim.status ===
                            "draft" ||
                        claim.status ===
                            "rejected"
                    );


                const canApproveReject =
                    canReview &&
                    claim.status ===
                        "submitted";


                const canPay =
                    canMarkPaid &&
                    claim.status ===
                        "approved";


                return (
                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            whitespace-nowrap
                        "
                    >

                        <CrudActions

                            showView={
                                typeof onView ===
                                "function"
                            }

                            onView={
                                onView
                                    ? () =>
                                        onView(claim)
                                    : undefined
                            }

                            viewTitle="View TA/DA Claim"


                            showEdit={
                                canEdit
                            }

                            onEdit={
                                canEdit &&
                                typeof onEdit ===
                                    "function"
                                    ? () =>
                                        onEdit(claim)
                                    : undefined
                            }

                            editTitle="Edit TA/DA Claim"


                            showApprove={
                                canApproveReject
                            }

                            onApprove={
                                canApproveReject &&
                                typeof onApprove ===
                                    "function"
                                    ? () =>
                                        onApprove(
                                            claim.id
                                        )
                                    : undefined
                            }

                            approveTitle="Approve TA/DA Claim"


                            showReject={
                                canApproveReject
                            }

                            onReject={
                                canApproveReject &&
                                typeof onReject ===
                                    "function"
                                    ? () =>
                                        onReject(
                                            claim.id
                                        )
                                    : undefined
                            }

                            rejectTitle="Reject TA/DA Claim"


                            showDelete={
                                canCancelClaim
                            }

                            onDelete={
                                canCancelClaim &&
                                typeof onCancel ===
                                    "function"
                                    ? () =>
                                        onCancel(
                                            claim.id
                                        )
                                    : undefined
                            }

                            deleteTitle="Cancel TA/DA Claim"

                        />


                        {/* =================================================
                            SUBMIT
                        ================================================= */}

                        {
                            canSubmit &&
                            typeof onSubmit ===
                                "function" && (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onSubmit(
                                            claim.id
                                        )
                                    }
                                    title="Submit TA/DA Claim"
                                    aria-label="Submit TA/DA Claim"
                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-indigo-200
                                        bg-indigo-50
                                        text-indigo-600
                                        transition-all
                                        duration-150
                                        hover:border-indigo-300
                                        hover:bg-indigo-100
                                        hover:text-indigo-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-indigo-200
                                    "
                                >

                                    <FiSend
                                        size={16}
                                        strokeWidth={2}
                                    />

                                </button>

                            )
                        }


                        {/* =================================================
                            MARK PAID
                        ================================================= */}

                        {
                            canPay &&
                            typeof onMarkPaid ===
                                "function" && (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onMarkPaid(
                                            claim.id
                                        )
                                    }
                                    title="Mark TA/DA Paid"
                                    aria-label="Mark TA/DA Paid"
                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-emerald-200
                                        bg-emerald-50
                                        text-emerald-600
                                        transition-all
                                        duration-150
                                        hover:border-emerald-300
                                        hover:bg-emerald-100
                                        hover:text-emerald-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-emerald-200
                                    "
                                >

                                    <FaRupeeSign
                                        size={15}
                                    />

                                </button>

                            )
                        }

                    </div>
                );
            },
        }),

    ];
};